/**
 * Bundle 71 - preset chips + saved views menu for registrations filters.
 *
 * Presets are hard-coded workflow shortcuts that map to filter dicts.
 * Saved views persist per-admin in `admin_saved_views` and can be
 * loaded / deleted.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Sparkles, Save, ChevronDown, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

/* Bundle 71 - preset ID -> filter object registry. */
export const PRESETS = {
  ready_to_issue: {
    label: 'Ready to issue tickets',
    icon: 'CheckCircle',
    filters: {
      payment_status: 'verified',
      photo_status: 'has_photo',
      social_status: 'has_social',
      vetting_status: 'approved',
      needs_follow_up: 'no',
      ticket_status: 'not_issued',
    },
  },
  needs_attention: {
    label: 'Needs my attention',
    filters: { needs_follow_up: 'yes' },
  },
  payment_follow_up: {
    label: 'Payment follow-up',
    filters: { payment_status: 'user_marked_paid' },
  },
  warm_reengagement: {
    label: 'Warm re-engagement',
    filters: { is_new_attendee: 'no', received_nudge: 'no' },
  },
  new_this_week: {
    label: 'New this week',
    filters: { signed_up_within_days: '7' },
  },
};

export const PresetChips = ({ onApply, activePresetId }) => (
  <div
    className="flex flex-wrap gap-1.5"
    data-testid="registrations-presets"
  >
    {Object.entries(PRESETS).map(([id, p]) => (
      <button
        key={id}
        type="button"
        onClick={() => onApply(id, p.filters)}
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold transition-colors ${
          activePresetId === id
            ? 'bg-primary-700 text-white border-primary-700'
            : 'bg-white text-primary-800 border-primary-200 hover:bg-primary-50'
        }`}
        data-testid={`preset-${id}`}
      >
        <Sparkles className="h-3 w-3" />
        {p.label}
      </button>
    ))}
  </div>
);

/* ---------------------------------------------------------------- */
/* Saved views menu                                                 */
/* ---------------------------------------------------------------- */
export const SavedViewsMenu = ({ page, eventId, currentFilters, onApply }) => {
  const [views, setViews] = useState([]);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [loaded, setLoaded] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get('/admin/saved-views', {
        params: { page, event_id: eventId || undefined },
      });
      setViews(data.views || []);
    } catch (e) {
      // Silent fail; menu still usable for save-new.
    } finally {
      setLoaded(true);
    }
  };
  useEffect(() => { reload(); }, [page, eventId]);

  const save = async () => {
    const trimmed = (name || '').trim();
    if (!trimmed) {
      toast.error('Give this view a name first');
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/saved-views', {
        page, name: trimmed, filter_json: currentFilters || {},
        event_id: eventId || null,
      });
      toast.success(`Saved view "${trimmed}"`);
      setName('');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this saved view?')) return;
    try {
      await api.delete(`/admin/saved-views/${id}`);
      await reload();
      toast.success('View deleted');
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline" size="sm"
          className="inline-flex items-center gap-1.5"
          data-testid="saved-views-menu-btn"
        >
          <Save className="h-3.5 w-3.5" /> Views
          {views.length > 0 ? (
            <Badge
              variant="outline"
              className="h-4 text-[10px] px-1 bg-neutral-100 text-neutral-700 border-neutral-200"
              data-testid="saved-views-count"
            >
              {views.length}
            </Badge>
          ) : null}
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end" className="w-64" data-testid="saved-views-dropdown"
      >
        <div className="px-2 pt-2 text-[10px] uppercase tracking-widest text-neutral-500">
          Save current filters as...
        </div>
        <div className="p-2 flex gap-1.5">
          <Input
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="View name" className="h-7 text-xs"
            data-testid="saved-view-name-input"
          />
          <Button
            size="sm" className="h-7 text-[11px]" onClick={save}
            disabled={saving} data-testid="saved-view-save-btn"
          >
            Save
          </Button>
        </div>
        <DropdownMenuSeparator />
        <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-neutral-500">
          My views
        </div>
        {views.length === 0 && loaded ? (
          <div className="px-3 py-2 text-xs text-neutral-500 italic">
            No saved views yet.
          </div>
        ) : (
          views.map((v) => (
            <DropdownMenuItem
              key={v.id}
              className="flex items-center justify-between text-xs"
              onSelect={(e) => { e.preventDefault(); onApply(v.filter_json || {}); }}
              data-testid={`saved-view-${v.id}`}
            >
              <span className="truncate max-w-[160px]">{v.name}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); remove(v.id); }}
                className="p-1 rounded hover:bg-red-50"
                data-testid={`saved-view-delete-${v.id}`}
              >
                <Trash2 className="h-3 w-3 text-red-600" />
              </button>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default { PresetChips, SavedViewsMenu, PRESETS };
